/**
 * CrmLossReasonFilter - Filtro de Motivos de Perda (multi-seleção)
 *
 * Permite selecionar múltiplos motivos de perda cadastrados no tenant.
 * Trigger mostra resumo (N motivos) com miniaturas coloridas — mesmo padrão
 * visual de CrmDealTagsFilter.
 *
 * @version 1.0.0
 * RELEASE: 2026-10-05 v1.1.0 - Gatilho do filtro segue o modelo de pill: sem a linha cinza da borda, fundo de superfície, sombra sutil e cantos arredondados (rounded-full).
 * RELEASE: 2026-07-11 v1.0.0 - Implementação inicial para listagem de Negócios.
 */

import React, { useState } from 'react';
import { Check, ChevronDown, XCircle, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { useCrmLossReasons } from '@/hooks/useCrmLossReasons';

/**
 * Props do componente
 * @param value - Array de IDs de motivos selecionados
 * @param onChange - Callback de mudança
 * @param className - Classes CSS adicionais
 */
interface CrmLossReasonFilterProps {
  value: string[];
  onChange: (reasonIds: string[]) => void;
  className?: string;
}

/**
 * Filtro de motivos de perda multi-seleção
 * Só considera motivos ativos (usuário raramente quer filtrar por inativos).
 */
export function CrmLossReasonFilter({ value, onChange, className }: CrmLossReasonFilterProps) {
  const [open, setOpen] = useState(false);
  const { activeReasons } = useCrmLossReasons(false);

  // Toggle individual (multi-seleção)
  const toggleReason = (id: string) => {
    if (value.includes(id)) {
      onChange(value.filter((r) => r !== id));
    } else {
      onChange([...value, id]);
    }
  };

  const clearAll = () => onChange([]);

  const selected = activeReasons.filter((r) => value.includes(r.id));
  const preview = selected.slice(0, 3);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className={cn('gap-1.5 font-normal border-0 bg-background rounded-full', className)}
        >
          <XCircle className="h-3 w-3 sm:h-3.5 sm:w-3.5 shrink-0 text-muted-foreground" />
          {selected.length > 0 ? (
            <span className="flex items-center gap-1 truncate">
              <span className="flex items-center -space-x-1">
                {preview.map((r) => (
                  <span
                    key={r.id}
                    className="w-2.5 h-2.5 rounded-full border border-background shrink-0"
                    style={{ backgroundColor: r.color || '#94a3b8' }}
                  />
                ))}
              </span>
              <span className="truncate">
                {selected.length} motivo{selected.length > 1 ? 's' : ''}
              </span>
            </span>
          ) : (
            <span className="truncate">Motivos de Perda</span>
          )}
          <ChevronDown className="h-3 w-3 opacity-50 shrink-0" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[280px] p-0" align="start">
        <Command>
          <CommandInput placeholder="Buscar motivo..." />
          <CommandList>
            <CommandEmpty>Nenhum motivo cadastrado.</CommandEmpty>
            <CommandGroup heading="Motivos de Perda">
              {activeReasons.map((reason) => {
                const isSelected = value.includes(reason.id);
                return (
                  <CommandItem
                    key={reason.id}
                    value={reason.name}
                    onSelect={() => toggleReason(reason.id)}
                    className="cursor-pointer"
                  >
                    <div
                      className={cn(
                        'mr-2 h-4 w-4 rounded border flex items-center justify-center',
                        isSelected ? 'border-primary bg-primary' : 'border-muted',
                      )}
                    >
                      {isSelected && <Check className="h-2.5 w-2.5 text-primary-foreground" />}
                    </div>
                    <span
                      className="w-2.5 h-2.5 rounded-full mr-2 shrink-0"
                      style={{ backgroundColor: reason.color || '#94a3b8' }}
                    />
                    <span className="truncate flex-1">{reason.name}</span>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
          {selected.length > 0 && (
            <div className="border-t p-2">
              <Button
                variant="ghost"
                size="sm"
                className="w-full h-7 text-xs text-muted-foreground"
                onClick={clearAll}
              >
                <X className="h-3 w-3 mr-1" />
                Limpar seleção
              </Button>
            </div>
          )}
        </Command>
      </PopoverContent>
    </Popover>
  );
}
